import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLang } from "@/i18n/LanguageProvider";

// Routes where the sticky CTA is redundant (already on a waitlist/founder flow)
const HIDDEN_ON = ["/waitlist", "/founders"];

export default function StickyCTA() {
  const { t } = useTranslation();
  const { prefix } = useLang();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    try {
      if (sessionStorage.getItem("ix.sticky.dismissed") === "1") setDismissed(true);
    } catch (_) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 700);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const hidden =
    dismissed ||
    HIDDEN_ON.some((p) => loc.pathname === p || loc.pathname.startsWith(`${p}/`));

  const dismiss = () => {
    try {
      sessionStorage.setItem("ix.sticky.dismissed", "1");
    } catch (_) {
      /* ignore */
    }
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      {visible && !hidden && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.9 }}
          transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
          className="fixed bottom-5 right-5 md:bottom-8 md:right-8 z-40 flex items-center gap-1"
          data-testid="sticky-cta"
        >
          <Link
            to={prefix("/waitlist")}
            className="group inline-flex items-center gap-2.5 px-5 py-3.5 rounded-full bg-lime text-ink font-semibold text-sm md:text-[15px] tracking-tight shadow-[0_20px_50px_-15px_rgba(232,255,0,0.6),0_10px_30px_-15px_rgba(45,13,94,0.5)] ring-1 ring-ink/10 hover:brightness-105 active:scale-[0.97] transition-all"
            data-testid="sticky-cta-link"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-forest opacity-70" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-forest" />
            </span>
            {t("nav.joinWaitlistLong")}
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.4} />
          </Link>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            data-testid="sticky-cta-dismiss"
            className="w-8 h-8 rounded-full bg-forest text-canvas hover:bg-forest-light grid place-items-center transition-colors shadow-lg"
          >
            <X className="w-3.5 h-3.5" strokeWidth={2.4} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
